import React from "react";
import { LoaderFunctionArgs } from "react-router-dom";

//components
import Button from "../../../../components/Button";
import Pagination from "../../../../components/Pagination";
import ScrollableTable from "../../../../components/TableLite/TableLiteScrollable";
import Config from "./Config";
import CreateFormModal from "./components/CreateFormModal";
import DeleteFormModal from "./components/DeleteFormModal";
import FormsCols from "./components/FormsTableCols";
import RelatedWorkflowsModal from "./components/RelatedWorkflowsModal";

//hooks
import { useCompanyForms, useRbacPrimaryRolePermissions } from "../../../../api/hooks";
import useSearchQueryParams from "../../../../hooks/useSearchQueryParams";

// queries
import { currentUserQuery, rbacPermissionsQuery, useCompanyFormsQuery } from "../../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../../api/queries/dataLoader";

//templates
import AdminTablePageTemplate from "../../../../pageTemplates/AdminTablePageTemplate";
import EmptySearchTableMessage from "../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";

const paginationLimit = 25;

export const adminReviewsFormsLoader = async ({ request }: LoaderFunctionArgs) => {
	const url = new URL(request.url);
	const searchParams = url.searchParams;
	const search = searchParams.get("search") || "";
	const sort = searchParams.get("name") || "name";
	const direction = searchParams.get("direction") || "asc";
	const pageNumber = searchParams.get("pageNumber") || "1";
	const queries: Array<() => SimplifiedQuery> = [];

	queries.push(() => rbacPermissionsQuery("review"));
	queries.push(() =>
		useCompanyFormsQuery({ search, sort, direction, page: Number(pageNumber), limit: paginationLimit })
	);

	return await dataLoader([[currentUserQuery], queries]);
};

const AdminReviewsForms = () => {
	const { data: rbac } = useRbacPrimaryRolePermissions("review");
	const { queryParams, handleQueryChange } = useSearchQueryParams({ schema: Config.adminFormsPageSchema });
	const emptyTableState = <EmptySearchTableMessage mainText="No Review Forms Found" />;
	const {
		sort: { name: sort, direction },
		setSort,
		searchValue,
		setDebouncedSearch,
		debouncedSearchValue,
		pageNumber = 1,
		setPageNumber,
	} = useInfoPageTemplateQueryState({
		defaultSortName: "name",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
	});
	const { data = { forms: [], total: 0 }, isLoading } = useCompanyForms({
		search: debouncedSearchValue,
		page: pageNumber,
		limit: paginationLimit,
		sort,
		direction,
	});
	const { forms = [], total = 0 } = data;
	const selectedRelatedWorkflow = forms.find((form) => form._id.toString() === queryParams?.relatedWorkflow);

	const canAddForm = rbac?.includes("reviews:createAdminCompanyForms");

	const mainContent = (
		<div>
			<ScrollableTable
				rows={forms}
				columns={FormsCols}
				isLoading={isLoading}
				emptyChildren={emptyTableState}
				activeSort={sort}
				activeSortDirection={direction}
				onColumnClick={(columnID, nextDirection) =>
					setSort({ name: columnID, direction: nextDirection as "asc" | "desc" })
				}
			/>
			<Pagination
				pageCount={Math.ceil(total / paginationLimit)}
				currentPage={pageNumber}
				onClick={(pageNumber: number) => setPageNumber(pageNumber)}
			/>
			<CreateFormModal
				isOpen={queryParams?.modal === "create"}
				handleClose={() => handleQueryChange({ modal: undefined })}
			/>
			<RelatedWorkflowsModal
				handleClose={() => handleQueryChange({ relatedWorkflow: undefined })}
				form={selectedRelatedWorkflow}
			/>
			<DeleteFormModal
				handleClose={() => handleQueryChange({ deleteFormId: undefined })}
				deleteFormId={queryParams?.deleteFormId || ""}
				deleteFormName={forms.find((form) => form._id.toString() === queryParams?.deleteFormId)?.name || ""}
			/>
		</div>
	);

	return (
		<AdminTablePageTemplate
			searchBar={
				<StandardToolbarActionRow
					searchInput={searchValue}
					onSearchInputChange={(value) => setDebouncedSearch(value)}
					searchPlaceholder="Search forms..."
				/>
			}
			actionButtons={[
				canAddForm ? (
					<Button
						key="addFormButton"
						onClick={() => handleQueryChange({ modal: "create" })}
						testid="addFormButton"
					>
						Create New Form
					</Button>
				) : null,
			]}
			mainContent={mainContent}
			pageName="Review Forms"
		/>
	);
};

export default AdminReviewsForms;
